// src/services/transactionService.js
import { db, storage } from '../config/firebase';
import { 
  collection, addDoc, updateDoc, deleteDoc, doc, 
  getDocs, getDoc, query, where, orderBy, serverTimestamp, 
  increment, Timestamp, arrayUnion 
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';

// Default category options
const DEFAULT_CATEGORIES = {
  income: [
    { id: 'sales', name: 'Sales Revenue', icon: '💰' },
    { id: 'services', name: 'Service Income', icon: '💼' },
    { id: 'ticket-sales', name: 'Ticket Sales', icon: '🎫' },
    { id: 'sponsorship', name: 'Sponsorships', icon: '🤝' },
    { id: 'donations', name: 'Donations', icon: '❤️' },
    { id: 'grants', name: 'Grants & Funding', icon: '🏆' },
    { id: 'merchandise', name: 'Merchandise Sales', icon: '👕' },
    { id: 'registration', name: 'Registration Fees', icon: '📝' },
    { id: 'other-income', name: 'Other Income', icon: '➕' }
  ],
  expense: [
    { id: 'venue', name: 'Venue Rental', icon: '🏢' },
    { id: 'catering', name: 'Catering & Food', icon: '🍽️' },
    { id: 'equipment', name: 'Equipment Rental', icon: '🎤' },
    { id: 'decoration', name: 'Decorations', icon: '🎨' },
    { id: 'entertainment', name: 'Entertainment', icon: '🎭' },
    { id: 'staff', name: 'Staff & Labor', icon: '👥' },
    { id: 'marketing', name: 'Marketing & Promotion', icon: '📢' },
    { id: 'printing', name: 'Printing & Materials', icon: '🖨️' },
    { id: 'transport', name: 'Transportation', icon: '🚗' },
    { id: 'insurance', name: 'Insurance', icon: '🛡️' },
    { id: 'permits', name: 'Permits & Licenses', icon: '📜' },
    { id: 'technology', name: 'Technology & AV', icon: '💻' },
    { id: 'supplies', name: 'Supplies & Materials', icon: '📦' },
    { id: 'professional', name: 'Professional Services', icon: '⚖️' },
    { id: 'other-expense', name: 'Other Expenses', icon: '➖' }
  ]
};

export const transactionService = {
  // ==================== CATEGORY MANAGEMENT ====================
  
  getDefaultCategories(type = null) {
    if (type) {
      return DEFAULT_CATEGORIES[type] || [];
    }
    return DEFAULT_CATEGORIES;
  },

  async getBookCategories(bookId) {
    try {
      const bookRef = doc(db, 'pb-books', bookId);
      const bookSnap = await getDoc(bookRef);
      
      if (!bookSnap.exists()) {
        return this.getDefaultCategories();
      }

      const bookData = bookSnap.data();
      const customCategories = bookData.customTransactionCategories || { income: [], expense: [] };
      
      // Merge default and custom categories
      return {
        income: [...DEFAULT_CATEGORIES.income, ...customCategories.income],
        expense: [...DEFAULT_CATEGORIES.expense, ...customCategories.expense]
      };
    } catch (error) {
      console.error('Error getting book categories:', error);
      return this.getDefaultCategories();
    }
  },

  async addCustomCategory(bookId, type, categoryName) {
    try {
      const categoryId = categoryName.toLowerCase().replace(/[^a-z0-9]+/g, '-');
      const newCategory = {
        id: `custom-${categoryId}-${Date.now()}`,
        name: categoryName,
        icon: type === 'income' ? '💵' : '💸',
        isCustom: true
      };

      const bookRef = doc(db, 'pb-books', bookId);
      
      // Add to the appropriate array (income or expense)
      await updateDoc(bookRef, {
        [`customTransactionCategories.${type}`]: arrayUnion(newCategory),
        updatedAt: serverTimestamp()
      });

      return newCategory;
    } catch (error) {
      console.error('Error adding custom category:', error);
      throw new Error('Failed to add custom category');
    }
  },

  // ==================== TRANSACTION CRUD ====================
  
  async createTransaction(bookId, transactionData) {
    try {
      const transactionsRef = collection(db, 'pb-transactions');
      const docRef = await addDoc(transactionsRef, {
        ...transactionData,
        bookId,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      // Update book analytics
      await this.updateBookAnalytics(bookId, transactionData);

      return { id: docRef.id, ...transactionData };
    } catch (error) {
      console.error('Error creating transaction:', error);
      throw new Error('Failed to create transaction');
    }
  },

  async getTransactions(bookId, filters = {}) {
    try {
      const transactionsRef = collection(db, 'pb-transactions');
      let q = query(
        transactionsRef, 
        where('bookId', '==', bookId),
        orderBy('transactionDate', 'desc')
      );

      // Apply filters
      if (filters.type) {
        q = query(q, where('type', '==', filters.type));
      }
      if (filters.category) {
        q = query(q, where('category', '==', filters.category));
      }
      if (filters.status) {
        q = query(q, where('status', '==', filters.status));
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        transactionDate: doc.data().transactionDate?.toDate(),
        createdAt: doc.data().createdAt?.toDate(),
        updatedAt: doc.data().updatedAt?.toDate()
      }));
    } catch (error) {
      console.error('Error getting transactions:', error);
      throw new Error('Failed to fetch transactions');
    }
  },

  async getTransactionById(transactionId) {
    try {
      const transactionRef = doc(db, 'pb-transactions', transactionId);
      const transactionDoc = await getDoc(transactionRef);
      
      if (!transactionDoc.exists()) {
        throw new Error('Transaction not found');
      }

      return {
        id: transactionDoc.id,
        ...transactionDoc.data(),
        transactionDate: transactionDoc.data().transactionDate?.toDate(),
        createdAt: transactionDoc.data().createdAt?.toDate(),
        updatedAt: transactionDoc.data().updatedAt?.toDate()
      };
    } catch (error) {
      console.error('Error getting transaction:', error);
      throw error;
    }
  },

  async updateTransaction(transactionId, updates) {
    try {
      const transactionRef = doc(db, 'pb-transactions', transactionId);
      await updateDoc(transactionRef, {
        ...updates,
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      console.error('Error updating transaction:', error);
      throw new Error('Failed to update transaction');
    }
  },

  async deleteTransaction(transactionId, bookId) {
    try {
      // Get transaction to delete files
      const transaction = await this.getTransactionById(transactionId);
      
      // Delete files from storage
      if (transaction.attachments?.length > 0) {
        await Promise.all(
          transaction.attachments.map(file => this.deleteFile(file.url))
        );
      }

      // Delete transaction document
      const transactionRef = doc(db, 'pb-transactions', transactionId);
      await deleteDoc(transactionRef);

      // Update book analytics
      await this.updateBookAnalytics(bookId, transaction, true);

      return { success: true };
    } catch (error) {
      console.error('Error deleting transaction:', error);
      throw new Error('Failed to delete transaction');
    }
  },

  // ==================== FILE UPLOAD ====================
  
  async uploadFile(file, bookId, transactionId) {
    try {
      // Validate file
      const maxSize = 10 * 1024 * 1024; // 10MB
      if (file.size > maxSize) {
        throw new Error('File size must be less than 10MB');
      }

      const allowedTypes = [
        'image/jpeg', 'image/png', 'image/gif', 'image/webp',
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'application/vnd.ms-excel',
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ];

      if (!allowedTypes.includes(file.type)) {
        throw new Error('File type not supported. Allowed: Images, PDF, Word, Excel');
      }

      // Create unique filename
      const timestamp = Date.now();
      const cleanFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const fileName = `pb-${bookId}/transactions/${transactionId}/${timestamp}_${cleanFileName}`;

      console.log(`Uploading file to: ${fileName}`);

      // Upload to Firebase Storage
      const storageRef = ref(storage, fileName);
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);

      console.log(`Upload successful: ${downloadURL}`);

      return {
        url: downloadURL,
        fileName: cleanFileName,
        fileType: file.type,
        fileSize: file.size,
        uploadedAt: new Date().toISOString(),
        path: fileName
      };
    } catch (error) {
      console.error('Error uploading file:', error);
      
      if (error.code === 'storage/unauthorized') {
        throw new Error('Upload permission denied. Please check your access rights.');
      } else if (error.code === 'storage/canceled') {
        throw new Error('Upload was canceled');
      } else if (error.code === 'storage/quota-exceeded') {
        throw new Error('Storage quota exceeded');
      } else {
        throw new Error(`Upload failed: ${error.message}`);
      }
    }
  },

  async uploadMultipleFiles(files, bookId, transactionId, onProgress) {
    const uploadPromises = files.map(async (file, index) => {
      try {
        const result = await this.uploadFile(file, bookId, transactionId);
        
        if (onProgress) {
          onProgress(index + 1, files.length);
        }
        
        return { success: true, index, ...result };
      } catch (error) {
        console.error(`Error uploading file ${file.name}:`, error);
        return { success: false, index, error: error.message, fileName: file.name };
      }
    });

    return await Promise.all(uploadPromises);
  },

  async deleteFile(fileUrl) {
    try {
      if (fileUrl && fileUrl.includes('firebase')) {
        const fileRef = ref(storage, fileUrl);
        await deleteObject(fileRef);
      }
      return { success: true };
    } catch (error) {
      console.error('Error deleting file:', error);
      return { success: false, error: error.message };
    }
  },

  // ==================== ANALYTICS ====================
  
  async updateBookAnalytics(bookId, transaction, isDelete = false) {
    try {
      const bookRef = doc(db, 'pb-books', bookId);
      const multiplier = isDelete ? -1 : 1;
      const amount = parseFloat(transaction.amount || 0) * multiplier;

      const updates = {
        transactionCount: increment(multiplier),
        updatedAt: serverTimestamp()
      };

      if (transaction.type === 'income') {
        updates.totalIncome = increment(amount);
      } else if (transaction.type === 'expense') {
        updates.totalExpenses = increment(amount);
      }

      await updateDoc(bookRef, updates);
      return { success: true };
    } catch (error) {
      console.error('Error updating book analytics:', error);
      return { success: false };
    }
  },

  async getTransactionAnalytics(bookId, dateRange = null) {
    try {
      const transactions = await this.getTransactions(bookId);
      
      // Filter by date range if provided
      let filteredTransactions = transactions;
      if (dateRange) {
        filteredTransactions = transactions.filter(t => {
          const date = t.transactionDate;
          return date >= dateRange.start && date <= dateRange.end;
        });
      }

      // Calculate analytics
      const analytics = {
        totalTransactions: filteredTransactions.length,
        totalIncome: 0,
        totalExpenses: 0,
        netBalance: 0,
        byCategory: {},
        byType: { income: 0, expense: 0 },
        byStatus: { pending: 0, completed: 0, cancelled: 0 },
        recentTransactions: filteredTransactions.slice(0, 10)
      };

      filteredTransactions.forEach(transaction => {
        const amount = parseFloat(transaction.amount || 0);
        
        // Type totals
        if (transaction.type === 'income') {
          analytics.totalIncome += amount;
          analytics.byType.income += amount;
        } else if (transaction.type === 'expense') {
          analytics.totalExpenses += amount;
          analytics.byType.expense += amount;
        }

        // Category breakdown
        if (transaction.category) {
          if (!analytics.byCategory[transaction.category]) {
            analytics.byCategory[transaction.category] = 0;
          }
          analytics.byCategory[transaction.category] += amount;
        }

        // Status counts
        if (transaction.status) {
          analytics.byStatus[transaction.status]++;
        }
      });

      analytics.netBalance = analytics.totalIncome - analytics.totalExpenses;

      return analytics;
    } catch (error) {
      console.error('Error getting transaction analytics:', error);
      throw new Error('Failed to fetch analytics');
    }
  }
};